import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Component } from '../../types';
import { pmTheme } from '../pm/pmTheme';

export interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status: 'completed' | 'active' | 'upcoming' | 'blocked';
  icon?: string;
  color?: string;
  children?: TimelineEvent[];
}

export interface TimelineProperties {
  events: TimelineEvent[];
  orientation: 'horizontal' | 'vertical';
  showConnectors: boolean;
  showDates: boolean;
  currentEventId?: string;
  variant: 'milestone' | 'gantt';
  compact: boolean;
}

export interface HorizontalTimelineProps {
  component: Component;
  isViewerMode?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  completed: pmTheme.success,
  active: pmTheme.primary,
  upcoming: pmTheme.textMuted,
  blocked: pmTheme.danger,
};

const TIMELINE_STATUSES = new Set<string>(['completed', 'active', 'upcoming', 'blocked']);
const TIMELINE_VARIANTS = new Set<string>(['milestone', 'gantt']);
const TIMELINE_ORIENTATIONS = new Set<string>(['horizontal', 'vertical']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTimelineStatus(value: unknown): value is TimelineEvent['status'] {
  return typeof value === 'string' && TIMELINE_STATUSES.has(value);
}

function isTimelineVariant(value: unknown): value is TimelineProperties['variant'] {
  return typeof value === 'string' && TIMELINE_VARIANTS.has(value);
}

function isTimelineOrientation(value: unknown): value is TimelineProperties['orientation'] {
  return typeof value === 'string' && TIMELINE_ORIENTATIONS.has(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function normalizeTimelineEvent(value: unknown, index: number): TimelineEvent | null {
  if (!isRecord(value)) {
    return null;
  }

  const title = optionalString(value.title) ?? optionalString(value.label);
  if (!title) {
    return null;
  }

  const event: TimelineEvent = {
    id: optionalString(value.id) ?? `event-${index}`,
    title,
    status: isTimelineStatus(value.status) ? value.status : 'upcoming',
  };
  const description = optionalString(value.description);
  const startDate = optionalString(value.startDate) ?? optionalString(value.date);
  const endDate = optionalString(value.endDate);
  const icon = optionalString(value.icon);
  const color = optionalString(value.color);

  if (description) event.description = description;
  if (startDate) event.startDate = startDate;
  if (endDate) event.endDate = endDate;
  if (icon) event.icon = icon;
  if (color) event.color = color;

  return event;
}

export const HorizontalTimeline: React.FC<HorizontalTimelineProps> = ({ component, isViewerMode = false }) => {
  const properties = component.properties ?? {};
  const rawEvents = properties.events;
  const events = useMemo(
    () => Array.isArray(rawEvents)
      ? rawEvents
        .map((event, index) => normalizeTimelineEvent(event, index))
        .filter((event): event is TimelineEvent => event !== null)
      : [],
    [rawEvents],
  );
  const variant = isTimelineVariant(properties.variant) ? properties.variant : 'milestone';
  const showConnectors = typeof properties.showConnectors === 'boolean' ? properties.showConnectors : true;
  const showDates = typeof properties.showDates === 'boolean' ? properties.showDates : true;
  const compact = properties.compact === true;
  const currentEventId = optionalString(properties.currentEventId);
  const requestedOrientation = isTimelineOrientation(properties.orientation) ? properties.orientation : 'horizontal';

  const containerRef = useRef<HTMLDivElement>(null);
  const [autoVertical, setAutoVertical] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setAutoVertical(entry.contentRect.width < 600);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const orientation = autoVertical ? 'vertical' : requestedOrientation;

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Inter, system-ui, sans-serif',
    overflow: 'auto',
    padding: compact ? '8px' : '16px',
    boxSizing: 'border-box',
    background: pmTheme.surface,
    color: pmTheme.text,
  };

  if (variant === 'gantt') {
    return (
      <div ref={containerRef} style={containerStyle} data-testid="timeline-component">
        <GanttVariant events={events} showDates={showDates} compact={compact} orientation={orientation} />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={containerStyle} data-testid="timeline-component">
      <MilestoneVariant
        events={events}
        showConnectors={showConnectors}
        showDates={showDates}
        compact={compact}
        currentEventId={currentEventId}
        orientation={orientation}
      />
    </div>
  );
};

interface MilestoneVariantProps {
  events: TimelineEvent[];
  showConnectors: boolean;
  showDates: boolean;
  compact: boolean;
  currentEventId?: string;
  orientation: 'horizontal' | 'vertical';
}

const MilestoneVariant: React.FC<MilestoneVariantProps> = ({
  events, showConnectors, showDates, compact, currentEventId, orientation,
}) => {
  if (events.length === 0) {
    return <div style={{ width: '100%', height: '100%' }} />;
  }

  if (orientation === 'vertical') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '20px', padding: '8px 0' }}>
        {events.map((event, idx) => {
          const color = event.color || STATUS_COLORS[event.status] || pmTheme.textMuted;
          const isCurrent = event.id === currentEventId;
          return (
            <div key={event.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r={isCurrent ? 10 : 8} fill={event.status === 'upcoming' ? pmTheme.surface : color} stroke={color} strokeWidth="2" />
                  {event.status === 'completed' && (
                    <path d="M8 12l3 3 5-5" fill="none" stroke={pmTheme.textInverse} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  )}
                  {event.status === 'blocked' && (
                    <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke={pmTheme.textInverse} strokeWidth="2" strokeLinecap="round" />
                  )}
                </svg>
                {showConnectors && idx < events.length - 1 && (
                  <div style={{ width: '2px', height: compact ? '16px' : '24px', backgroundColor: pmTheme.border, marginTop: '4px' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: isCurrent ? 600 : 500, color: pmTheme.text }}>{event.title}</div>
                {event.description && <div style={{ fontSize: '12px', color: pmTheme.textMuted, marginTop: '2px' }}>{event.description}</div>}
                {showDates && event.startDate && (
                  <div style={{ fontSize: '11px', color: pmTheme.textSubtle, marginTop: '2px' }}>
                    {new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Horizontal milestone
  const dotSize = compact ? 16 : 24;
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <svg width="100%" height={compact ? '80' : '120'} viewBox={`0 0 ${events.length * 160} ${compact ? 80 : 120}`} preserveAspectRatio="xMidYMid meet">
        {/* Main axis line */}
        <line x1="40" y1={compact ? 40 : 60} x2={events.length * 160 - 40} y2={compact ? 40 : 60} stroke={pmTheme.border} strokeWidth="2" />

        {events.map((event, idx) => {
          const cx = 40 + idx * 160 + 40;
          const cy = compact ? 40 : 60;
          const color = event.color || STATUS_COLORS[event.status] || pmTheme.textMuted;
          const isCurrent = event.id === currentEventId;
          const r = isCurrent ? dotSize / 2 + 2 : dotSize / 2;
          const labelY = idx % 2 === 0 ? cy - r - 8 : cy + r + 16;

          return (
            <g key={event.id}>
              {/* Connector to next */}
              {showConnectors && idx < events.length - 1 && (
                <line x1={cx + r + 4} y1={cy} x2={cx + 160 - r - 4} y2={cy} stroke={event.status === 'completed' ? color : pmTheme.border} strokeWidth="2" strokeDasharray={event.status === 'upcoming' ? '4 4' : 'none'} />
              )}
              {/* Status dot */}
              <circle cx={cx} cy={cy} r={r} fill={event.status === 'upcoming' ? pmTheme.surface : color} stroke={color} strokeWidth="2" />
              {event.status === 'completed' && (
                <path d={`M${cx - 4} ${cy}l3 3 5-5`} fill="none" stroke={pmTheme.textInverse} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              )}
              {event.status === 'active' && isCurrent && (
                <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke={color} strokeWidth="1" opacity="0.3">
                  <animate attributeName="r" values={`${r + 2};${r + 6};${r + 2}`} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
              {event.status === 'blocked' && (
                <g>
                  <line x1={cx - 3} y1={cy - 3} x2={cx + 3} y2={cy + 3} stroke={pmTheme.textInverse} strokeWidth="2" strokeLinecap="round" />
                  <line x1={cx + 3} y1={cy - 3} x2={cx - 3} y2={cy + 3} stroke={pmTheme.textInverse} strokeWidth="2" strokeLinecap="round" />
                </g>
              )}
              {/* Label */}
              <text x={cx} y={labelY} textAnchor="middle" fontSize={compact ? '10' : '12'} fontWeight={isCurrent ? '600' : '500'} fill={pmTheme.text}>
                {event.title.length > 18 ? event.title.substring(0, 16) + '…' : event.title}
              </text>
              {/* Date */}
              {showDates && event.startDate && (
                <text x={cx} y={labelY + (idx % 2 === 0 ? -12 : 14)} textAnchor="middle" fontSize="10" fill={pmTheme.textSubtle}>
                  {new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

interface GanttVariantProps {
  events: TimelineEvent[];
  showDates: boolean;
  compact: boolean;
  orientation: 'horizontal' | 'vertical';
}

const GanttVariant: React.FC<GanttVariantProps> = ({ events, showDates, compact }) => {
  const { minDate, maxDate } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    events.forEach(e => {
      if (e.startDate) min = Math.min(min, new Date(e.startDate).getTime());
      if (e.endDate) max = Math.max(max, new Date(e.endDate).getTime());
      else if (e.startDate) max = Math.max(max, new Date(e.startDate).getTime() + 86400000 * 30);
    });
    if (!isFinite(min)) min = Date.now();
    if (!isFinite(max)) max = min + 86400000 * 365;
    return { minDate: min, maxDate: max };
  }, [events]);

  if (events.length === 0) {
    return <div style={{ width: '100%', height: '100%' }} />;
  }

  const barHeight = compact ? 20 : 28;
  const rowHeight = compact ? 28 : 36;
  const svgHeight = events.length * rowHeight + 30;

  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <svg width="100%" height={svgHeight} viewBox={`0 0 800 ${svgHeight}`} preserveAspectRatio="xMinYMin meet">
        {/* Date axis */}
        {showDates && (
          <g>
            <line x1="120" y1={svgHeight - 20} x2="780" y2={svgHeight - 20} stroke={pmTheme.border} strokeWidth="1" />
            {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
              const x = 120 + pct * 660;
              const date = new Date(minDate + pct * (maxDate - minDate));
              return (
                <g key={i}>
                  <line x1={x} y1={svgHeight - 24} x2={x} y2={svgHeight - 16} stroke={pmTheme.borderSubtle} strokeWidth="1" />
                  <text x={x} y={svgHeight - 4} textAnchor="middle" fontSize="10" fill={pmTheme.textSubtle}>
                    {date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {/* Bars */}
        {events.map((event, idx) => {
          const start = event.startDate ? new Date(event.startDate).getTime() : minDate;
          const end = event.endDate ? new Date(event.endDate).getTime() : start + 86400000 * 30;
          const x = 120 + ((start - minDate) / (maxDate - minDate)) * 660;
          const width = Math.max(20, ((end - start) / (maxDate - minDate)) * 660);
          const y = idx * rowHeight + 4;
          const color = event.color || STATUS_COLORS[event.status] || pmTheme.textMuted;

          return (
            <g key={event.id}>
              {/* Label */}
              <text x="4" y={y + barHeight / 2 + 4} fontSize={compact ? '11' : '12'} fill={pmTheme.text} fontWeight="500">
                {event.title.length > 14 ? event.title.substring(0, 12) + '…' : event.title}
              </text>
              {/* Bar */}
              <rect x={x} y={y} width={width} height={barHeight} rx="4" fill={color} opacity={event.status === 'upcoming' ? 0.4 : 0.8} />
              {event.status === 'active' && (
                <rect x={x} y={y} width={width * 0.5} height={barHeight} rx="4" fill={color} opacity="1" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

HorizontalTimeline.displayName = 'HorizontalTimeline';
